/**
 * ONE ITEM CARD, EVERYWHERE.
 *
 * Phase 21. The Fence card printed an item's three authored stats and a business's net per day;
 * the Black Market card printed none of it, the Kit printed a power number and a Condition, and a
 * drop on the crime plate printed a name. Four surfaces, four cards, one item. This is the card,
 * and the four surfaces put their own buttons under it.
 *
 * What it draws, when the thing has it: the rarity, the kind and the name and the line, the art,
 * the labelled stat triple with the power beside it and the delta against what the viewer is
 * wearing in that slot, a business's net per day and upkeep and how long it takes to repay its
 * price, Condition, and book against price. Every number came off the wire: the stats and the
 * power are the catalogue's, the worn power is the viewer's kit, the price is the seller's.
 *
 * THERE IS NO LEVEL GATE ON THE CARD, because there is none in the content. GAME-DESIGN 9.1 says
 * every item carries a rank or level threshold to equip; `content/items/*.json` carries `tier`
 * and no threshold, and `PlayerDO.equip` checks none. DESIGN-DELTAS 1ab.2.
 */

import type { ReactNode } from 'react'
import type { CatalogItem, Cents, Currency, Rarity } from '@ashport/shared'
import { A } from '../art'
import { Icon } from './Icon'
import { Art, Chip, Meter, Row, Spacer } from './Kit'
import { sz } from './viewport'
import { exact } from '../game/state'

/** What the authored stat triple MEANS, per category. CONTENT-BIBLE 8.2 to 8.4. */
export const STAT_LABELS: Record<'weapons' | 'armor' | 'vehicles', [string, string, string]> = {
  weapons: ['Damage', 'Accuracy', 'Conceal'],
  armor: ['Protect', 'Mobility', 'Conceal'],
  vehicles: ['Speed', 'Capacity', 'Notice'],
}

export const CUR: Record<Currency, { c: string; label: string }> = {
  dirty: { c: 'var(--dirty)', label: 'Dirty Cash' },
  clean: { c: 'var(--clean)', label: 'Clean Cash' },
}

export const condColor = (v: number) => (v >= 85 ? 'var(--dirty)' : v >= 70 ? 'var(--gold)' : 'var(--heat)')

/** The authored capability triple, or null for anything the bible priced instead of statted. */
export const gearOf = (def: CatalogItem | undefined) =>
  def && (def.category === 'weapons' || def.category === 'armor' || def.category === 'vehicles') ? def : null

export const businessOf = (def: CatalogItem | undefined) => (def && def.category === 'business' ? def : null)

/** Eras of net income to pay a price back. GAME-DESIGN 9.7 sized the fronts on this number. */
export const ERA_DAYS = 84
export function repayEras(price: Cents, netDaily: Cents): number | null {
  if (netDaily <= 0) return null
  return price / (netDaily * ERA_DAYS)
}
export function repayLine(price: Cents, netDaily: Cents): string {
  const eras = repayEras(price, netDaily)
  if (eras === null) return 'never repays'
  if (eras < 1 / 7) return `repays in ${Math.max(1, Math.ceil(eras * ERA_DAYS))} days`
  if (eras < 1) return `repays in ${Math.ceil(eras * 12)} weeks`
  return `repays in ${eras.toFixed(1)} Eras`
}

export function ItemCard({
  def,
  name,
  sub,
  kind,
  rarity,
  artId,
  price,
  cur,
  baseValue,
  cond,
  qty,
  worn,
  equipped,
  flag,
  children,
  compact,
}: {
  /** The catalogue entry, for the stats or the income. Absent for an item the catalogue cannot name. */
  def?: CatalogItem
  name: string
  sub: string
  kind?: string
  rarity: Rarity
  artId: string
  price?: Cents
  cur?: Currency
  /** Book value, printed against the price when both are present. */
  baseValue?: Cents
  cond?: number | null
  qty?: number
  /** The viewer's worn power in this item's slot, so a gear card can say what changes. */
  worn?: { power: number; name: string } | null
  equipped?: boolean
  /** A small word at the top right: Yours, Worn, Locked. */
  flag?: ReactNode
  /** The surface's own controls, under the card. */
  children?: ReactNode
  /** A shorter card for a rail: no art, one line of stats. */
  compact?: boolean
}) {
  const gear = gearOf(def)
  const biz = businessOf(def)
  const delta = gear && worn ? gear.power - worn.power : null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
      <Row>
        <Chip r={rarity}>{rarity}</Chip>
        {equipped && <Chip c="var(--clean)">Worn</Chip>}
        <Spacer />
        {flag}
      </Row>

      {kind && (
        <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 7 }}>
          {kind}
        </div>
      )}
      <div
        className="t-head"
        style={{
          fontSize: sz(12.5),
          color: 'var(--ink-hi)',
          letterSpacing: '.05em',
          marginTop: kind ? 1 : 6,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {name}
      </div>
      <div
        style={{ fontSize: sz(10.5), color: 'var(--ink-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        title={sub}
      >
        {sub}
      </div>

      {!compact && (
        <Art
          src={A(artId as Parameters<typeof A>[0], 'market.rarity.plate_common')}
          alt={`${name}. ${sub}`}
          scrim="none"
          style={{ height: 84, marginTop: 8, flex: 'none', border: '1px solid var(--line-soft)' }}
        />
      )}

      {/* ---- what it does ------------------------------------------------ */}
      {gear && (
        <div style={{ marginTop: 8 }}>
          <Row gap={0}>
            {STAT_LABELS[gear.category].map((label, i) => (
              <div key={label} style={{ flex: 1, minWidth: 0, textAlign: i === 0 ? 'left' : i === 1 ? 'center' : 'right' }}>
                <div className="t-label" style={{ fontSize: sz(7.5) }}>{label}</div>
                <div className="t-mono" style={{ fontSize: sz(11), color: 'var(--ink-hi)' }}>
                  {gear.statMode === 'modifier' && gear.stats[i] > 0 ? '+' : ''}
                  {gear.stats[i]}
                </div>
              </div>
            ))}
          </Row>
          {/*
            * POWER, AND WHAT CHANGES. The first stat is what `power()` reads (shared/formulas.ts),
            * scaled by Condition when worn. The delta is against the viewer's worn item in the
            * same slot, which is the one comparison a buyer actually makes.
            */}
          <Row gap={6} style={{ marginTop: 5, paddingTop: 5, borderTop: '1px solid var(--line-soft)' }}>
            <Icon n="fist" s={11} c="var(--gold)" />
            <span className="t-label" style={{ fontSize: sz(8.5) }}>Power</span>
            <span className="t-num" style={{ fontSize: sz(12), color: 'var(--ink-hi)' }}>
              {gear.power}
            </span>
            <Spacer />
            {worn === null ? (
              <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--dirty)' }}>slot empty</span>
            ) : delta !== null ? (
              <span
                className="t-mono"
                title={`You are wearing ${worn?.name} at ${worn?.power}`}
                style={{ fontSize: sz(9.5), color: delta > 0 ? 'var(--dirty)' : delta < 0 ? 'var(--heat)' : 'var(--ink-dim)' }}
              >
                {delta > 0 ? `+${delta}` : delta === 0 ? 'same' : delta} vs worn
              </span>
            ) : null}
          </Row>
        </div>
      )}

      {biz && (
        <div style={{ marginTop: 8 }}>
          <Row style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>Net per day</span>
            <span className="t-mono" style={{ fontSize: sz(11), color: 'var(--clean)' }}>{exact(biz.netDaily)}</span>
          </Row>
          <Row style={{ justifyContent: 'space-between', marginTop: 2 }}>
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>Upkeep per day</span>
            <span className="t-mono" style={{ fontSize: sz(11), color: 'var(--heat)' }}>{exact(biz.upkeepDaily)}</span>
          </Row>
          {/* THE DECISION. A front is a price against a rate, and the rate against the price is
              the number the property game was played on. GAME-DESIGN 9.7's own table. */}
          {price !== undefined && (
            <Row style={{ justifyContent: 'space-between', marginTop: 2 }}>
              <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>At this price</span>
              <span className="t-mono" style={{ fontSize: sz(11), color: 'var(--gold)' }}>{repayLine(price, biz.netDaily)}</span>
            </Row>
          )}
        </div>
      )}

      {cond !== undefined && cond !== null && (
        <div style={{ marginTop: 8 }}>
          <Row style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>Condition</span>
            <span className="t-mono" style={{ fontSize: sz(11), color: condColor(cond) }}>{cond}%</span>
          </Row>
          <div style={{ marginTop: 4 }}>
            <Meter v={cond} a="var(--brass-deep)" b={condColor(cond)} h={4} />
          </div>
        </div>
      )}

      {qty !== undefined && !gear && !biz && (
        <Row style={{ justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>Quantity</span>
          <span className="t-mono" style={{ fontSize: sz(11), color: 'var(--ink-hi)' }}>{qty}</span>
        </Row>
      )}

      {/* ---- what it costs ----------------------------------------------- */}
      {price !== undefined && cur !== undefined && (
        <Row style={{ marginTop: 9, alignItems: 'flex-end' }}>
          {baseValue !== undefined && (
            <div style={{ minWidth: 0 }}>
              <div className="t-label" style={{ fontSize: sz(8) }}>Book {exact(baseValue)}</div>
              {price !== baseValue && (
                <div className="t-mono" style={{ fontSize: sz(9.5), color: price > baseValue ? 'var(--heat)' : 'var(--dirty)' }}>
                  {price > baseValue ? `${Math.round((price / baseValue - 1) * 100)}% over` : `${Math.round((1 - price / baseValue) * 100)}% under`}
                </div>
              )}
            </div>
          )}
          <Spacer />
          <div style={{ textAlign: 'right' }}>
            <div className="t-num" style={{ fontSize: 17, color: CUR[cur].c, letterSpacing: '.02em' }}>
              {exact(price)}
            </div>
            <div className="t-label" style={{ fontSize: sz(8), color: CUR[cur].c }}>{CUR[cur].label}</div>
          </div>
        </Row>
      )}

      {children}
    </div>
  )
}
